import { useState, useRef } from 'react';
import type { FileAttachment } from '../api';
import { agentApi } from '../api';

/**
 * UI-side attachment representation — document_id 단일 모드.
 *
 * file pick 시 즉시 `POST /documents`로 업로드 → 서버 응답의 `document_id`만
 * 클라이언트가 보관. base64 인라인 입력은 서버 schema 단계에서 reject되므로
 * 본 훅은 base64 변환 path를 제공하지 않는다.
 *
 * Convert to wire format with {@link toFileAttachment} before sending.
 */
export interface AttachedFile {
  preview: string; // image 미리보기 URL (non-image / document_id 첨부는 '')
  media_type: string;
  document_id: string;
  name: string;
}

/** Convert an UI {@link AttachedFile} to the wire {@link FileAttachment}. */
export function toFileAttachment(att: AttachedFile): FileAttachment {
  return { document_id: att.document_id, name: att.name };
}

/**
 * MIME types the agent server accepts for chat attachments.
 *
 * Single source of truth shared between:
 * - `<input accept=...>` on the paperclip picker ({@link SUPPORTED_ACCEPT_STRING})
 * - file pick validator in this hook
 * - {@link DocumentPickerModal}'s status filter (disables rows that would fail
 *   server-side anyway, avoiding a pointless round-trip + error toast)
 *
 * Keep this list in sync with agent-server's FileValidator — mismatches here
 * mean users either can't pick supported files, or can pick unsupported ones
 * and see a silent failure.
 */
export const SUPPORTED_MEDIA_TYPES: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

/** Comma-joined form for `<input accept>`. */
export const SUPPORTED_ACCEPT_STRING = SUPPORTED_MEDIA_TYPES.join(',');

/** Returns true if the given MIME is accepted by the server for chat attachment. */
export function isSupportedMediaType(mime: string): boolean {
  return SUPPORTED_MEDIA_TYPES.includes(mime);
}

interface UseFileAttachmentParams {
  userId: string;
  onError: (msg: string) => void;
}

// refactor/96: 클라이언트 측 cap (MAX_FILES, MAX_FILE_SIZE, MAX_TOTAL_SIZE) 전체 제거.
// BE (`POST /api/v1/users/:user_id/documents`) 가 tier·model 한도 단일 출처로 검증하고
// `errors[].error_code` 로 caller 분기. 클라이언트는 sanity check 없이 BE 응답에 위임.

export function useFileAttachment({ userId, onError }: UseFileAttachmentParams) {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    // userId 가드 — PlaygroundChat은 user 선택 전에 첨부 못하지만 React state
    // race로 빈 string이 들어오면 admin API가 404로 응답하는 침묵 실패 케이스 방지.
    if (!userId) {
      onError('사용자를 먼저 선택해 주세요.');
      return;
    }

    const filesToProcess = Array.from(files);
    // Reset input so the same file can be re-selected
    e.target.value = '';

    for (const file of filesToProcess) {
      if (!isSupportedMediaType(file.type)) {
        onError('지원하지 않는 파일 형식입니다. (JPEG, PNG, WebP, PDF)');
        continue;
      }

      try {
        const uploadResp = await agentApi.uploadDocument(userId, file);
        // BatchDocumentUploadResponse: 단일 file을 보내도 results[] 형태로 반환.
        // errors가 있으면 첫 errors[0].detail로 사용자에게 알림.
        if (uploadResp.errors && uploadResp.errors.length > 0) {
          const err = uploadResp.errors[0];
          onError(`업로드 실패 (${err.error_code}): ${err.detail}`);
          continue;
        }
        const result = uploadResp.results?.[0];
        if (!result?.document_id) {
          onError(`업로드 실패: ${file.name} (응답에 document_id 없음)`);
          continue;
        }
        const preview = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : '';
        setAttachedFiles((prev) => [
          ...prev,
          {
            preview,
            media_type: file.type,
            document_id: result.document_id,
            name: file.name,
          },
        ]);
      } catch (err) {
        onError(`업로드 실패: ${file.name}`);
      }
    }
  };

  /** Attach a previously uploaded document by its ID — no re-upload. */
  const attachDocumentId = (params: {
    documentId: string;
    name: string;
    media_type: string;
  }) => {
    setAttachedFiles((prev) => {
      return [
        ...prev,
        {
          preview: '',
          media_type: params.media_type,
          document_id: params.documentId,
          name: params.name,
        },
      ];
    });
  };

  const removeAttachedFile = (index: number) => {
    setAttachedFiles((prev) => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  return {
    attachedFiles,
    setAttachedFiles,
    fileInputRef,
    handleFileAttach,
    attachDocumentId,
    removeAttachedFile,
  };
}
